/**
 * Communications Query Keys
 *
 * Conversation threads consumed by the Communications tab. Mirrors
 * `GET /matters/:matterId/communications/...` endpoints.
 *
 * **Every key is owner-scoped** (LF-6001 / LEADS-34). A thread belongs to
 * either a matter or a lead, and their ids come from separate auto-increment
 * columns — so the owner *type* is part of the key, never just the id. Passing
 * a bare number is a type error by design: it was the untyped positional
 * `matterId` at index 2 that let a lead id collide one-for-one with a matter id.
 *
 * Prefer `threadItemsForOwner()` / `threadItemsPending()` over hand-writing a
 * prefix like `[...communicationsKeys.all, 'thread-items', id]`. A hand-written
 * literal is invisible to the type checker, so a change to the tuple shape
 * silently stops the thread refreshing with no compile error — exactly the
 * drift these factories exist to prevent.
 */

import type {
  ConversationListFilter,
  ConversationListSort,
  ParticipantType
} from '@/lib/services/communications/types';
import {
  threadOwnerSegments,
  type ThreadOwner
} from '@/lib/services/communications/owner';

export const communicationsKeys = {
  /** Root key for all communications queries */
  all: ['communications'] as const,

  /**
   * The lazily computed AI reply offer for a thread's latest inbound message
   * (AICOMMS-4). A query (VALE-1883), so the composer and an early click on its
   * placeholder row share one request rather than each computing the offer.
   */
  replyOffer: (
    matterId: number,
    participantType: ParticipantType,
    participantId: number
  ) =>
    [
      ...communicationsKeys.all,
      'reply-offer',
      matterId,
      participantType,
      participantId
    ] as const,

  /** Base key for one owner's thread lists */
  threads: (owner: ThreadOwner) =>
    [
      ...communicationsKeys.all,
      'threads',
      ...threadOwnerSegments(owner)
    ] as const,

  /** Filtered thread list */
  threadList: (
    owner: ThreadOwner,
    filters?: {
      filter?: ConversationListFilter;
      sort?: ConversationListSort;
      search?: string;
    }
  ) =>
    filters
      ? ([...communicationsKeys.threads(owner), filters] as const)
      : communicationsKeys.threads(owner),

  /**
   * Every thread-items entry for one owner, whichever participant.
   *
   * The prefix that mutations invalidate when a change could touch any thread
   * on the owner (a send, a correspondence edit, a promote-to-Documents).
   */
  threadItemsForOwner: (owner: ThreadOwner) =>
    [
      ...communicationsKeys.all,
      'thread-items',
      ...threadOwnerSegments(owner)
    ] as const,

  /** Thread items for a specific participant */
  threadItems: (
    owner: ThreadOwner,
    participantType: ParticipantType,
    participantId: number
  ) =>
    [
      ...communicationsKeys.threadItemsForOwner(owner),
      participantType,
      participantId
    ] as const,

  /**
   * Placeholder key for a thread-items query with no participant selected yet.
   * Sits under the owner's prefix so the same invalidations clear it.
   */
  threadItemsPending: (owner: ThreadOwner) =>
    [...communicationsKeys.threadItemsForOwner(owner), 'pending'] as const,

  /**
   * Whether this viewer can search their connected Outlook mailbox from a given
   * thread (VALE-1217).
   *
   * Deliberately **outside** the `thread-items` prefix: it answers a question
   * about the viewer's mailbox and the participant's address book entry, neither
   * of which an import changes, so the invalidation that follows an import must
   * not clear it and send every open thread back to the network.
   */
  outlookSearchAvailability: (
    owner: ThreadOwner,
    participantType: ParticipantType,
    participantId: number
  ) =>
    [
      ...communicationsKeys.all,
      'outlook-search-availability',
      ...threadOwnerSegments(owner),
      participantType,
      participantId
    ] as const,

  /**
   * One participant's Outlook search results (VALE-1217).
   *
   * Paged with `useInfiniteQuery`, and **never persisted beyond the dialog's
   * lifetime**: the picker removes this key on close, because a cached page of
   * somebody's mailbox is exactly the retention the feature promises not to do.
   */
  outlookSearch: (
    owner: ThreadOwner,
    participantType: ParticipantType,
    participantId: number
  ) =>
    [
      ...communicationsKeys.all,
      'outlook-search',
      ...threadOwnerSegments(owner),
      participantType,
      participantId
    ] as const
} as const;
